import { Injectable } from '@angular/core';
import { DecimalFormatOptions, IntegerFormatOptions } from 'interfaces-lib';
import { Condition } from '../utils/condition';
import {
  AiAssistFieldOptions,
  DateFormatOptions,
  ImageFieldOptions,
  SortOption,
  StringFormatOptions,
} from '../utils/fieldconfig';
import { systemDefaults } from '../utils/fieldconfighelper';
import { FieldContext } from '../utils/fieldcontext';
import { ApplicationStoreService } from './application-store.service';
import { LoggingService } from './logging.service';

// TODO: find a better way to match FieldContext - UtilityTypes? https://www.typescriptlang.org/docs/handbook/utility-types.html
export type SerializedFieldContext = {
  namespace: string;
  entity: string;
  path: string;
  dataType?: string; // legacy fields (1.4.0 and earlier will not have this field)
  conditions?: Condition[];
  dateFormatOptions?: DateFormatOptions;
  integerFormat?: IntegerFormatOptions;
  decimalFormat?: DecimalFormatOptions;
  sortOption?: SortOption;
  limit?: number;
  useDisplayValue?: boolean; // in BH there is a value -> "display value" mapping - by default we use value
  contentControlId: number;
  stringFormat?: StringFormatOptions;
  defaultEmptyValue?: string;
  imageFieldConfig?: ImageFieldOptions;
  aiAssistFieldConfig?: AiAssistFieldOptions;
};

@Injectable({ providedIn: 'root' })
export class ContentControlToFieldMapService {
  private static FIELD_CONTEXTS_KEY = 'kp-field-contexts-v1';

  constructor(private applicationStoreService: ApplicationStoreService, private loggingService: LoggingService) {}

  get(contentControlId: number): SerializedFieldContext | undefined {
    const serializedFieldContexts = this.applicationStoreService.get(
      ContentControlToFieldMapService.FIELD_CONTEXTS_KEY
    ) as SerializedFieldContext[];
    if (!serializedFieldContexts) {
      return undefined;
    }

    return serializedFieldContexts.find((sfc) => sfc.contentControlId === contentControlId);
  }

  getAll(contentControlIds: number[]): SerializedFieldContext[] {
    const serializedFieldContexts = this.applicationStoreService.get(
      ContentControlToFieldMapService.FIELD_CONTEXTS_KEY
    ) as SerializedFieldContext[];
    if (!serializedFieldContexts) {
      return [];
    }

    const results: SerializedFieldContext[] = [];
    contentControlIds.forEach((id) => {
      const found = serializedFieldContexts.find((sfc) => sfc.contentControlId === id);
      if (found) {
        results.push(found);
      }
    });
    return results;
  }

  filter(contentControls: Word.ContentControl[]): Word.ContentControl[] {
    const serializedFieldContexts = this.applicationStoreService.get(
      ContentControlToFieldMapService.FIELD_CONTEXTS_KEY
    ) as SerializedFieldContext[];
    if (!serializedFieldContexts) {
      return [];
    }

    const isPresent = (contentControlId: number): boolean =>
      serializedFieldContexts.find((sfc) => sfc.contentControlId === contentControlId) !== undefined;

    return contentControls.filter((wcc) => isPresent(wcc.id));
  }

  async set(contentControlId: number, fieldContext: FieldContext): Promise<void> {
    let serializedFieldContexts = this.applicationStoreService.get(
      ContentControlToFieldMapService.FIELD_CONTEXTS_KEY
    ) as SerializedFieldContext[];
    if (!serializedFieldContexts) {
      serializedFieldContexts = [];
    }

    const idx = serializedFieldContexts.findIndex((sfc) => sfc.contentControlId === contentControlId);
    if (idx > -1) {
      serializedFieldContexts.splice(idx, 1);
    }

    serializedFieldContexts.push({
      contentControlId: contentControlId,
      namespace: fieldContext.namespace,
      entity: fieldContext.entity.entity,
      path: fieldContext.field.path,
      dataType: fieldContext.field.type,
      conditions: fieldContext.conditions,
      dateFormatOptions: fieldContext.dateFormatOptions,
      integerFormat: fieldContext.integerFormat,
      decimalFormat: fieldContext.decimalFormat,
      sortOption: fieldContext.sortOption,
      limit: fieldContext.limit,
      useDisplayValue: fieldContext.useDisplayValue,
      stringFormat: fieldContext.stringFormat,
      defaultEmptyValue: fieldContext.defaultEmptyValue,
      imageFieldConfig: fieldContext.imageFieldConfig,
      aiAssistFieldConfig: fieldContext.aiAssistFieldConfig,
    });

    await this.applicationStoreService.set(ContentControlToFieldMapService.FIELD_CONTEXTS_KEY, serializedFieldContexts);
  }

  async delete(contentControlId: number): Promise<boolean> {
    const serializedFieldContexts = this.applicationStoreService.get(
      ContentControlToFieldMapService.FIELD_CONTEXTS_KEY
    ) as SerializedFieldContext[];
    if (!serializedFieldContexts) {
      return false;
    }

    const idx = serializedFieldContexts.findIndex((sfc) => sfc.contentControlId === contentControlId);
    if (idx < 0) {
      return false;
    }

    console.debug(`Deleting field context with id ${contentControlId}`);
    serializedFieldContexts.splice(idx, 1);

    await this.applicationStoreService.set(ContentControlToFieldMapService.FIELD_CONTEXTS_KEY, serializedFieldContexts);

    return true;
  }

  async upgrade(): Promise<void> {
    console.debug('ContentControlToFieldMapService upgrade.');

    const serializedFieldContexts = this.applicationStoreService.get(ContentControlToFieldMapService.FIELD_CONTEXTS_KEY) as any[];
    if (!serializedFieldContexts) {
      return;
    }

    let upgraded = false;
    serializedFieldContexts.forEach((ssf) => {
      if (ssf.condition && !ssf.conditions) {
        ssf.conditions = [ssf.condition];
        upgraded = true;
      }

      if (ssf.dateFormat && !ssf.dateFormatOptions) {
        ssf.dateFormatOptions = {
          ...systemDefaults.formatting.dateFormatOptions,
          dateFormat: ssf.dateFormat,
        };
        upgraded = true;
      }
    });

    if (upgraded) {
      await this.applicationStoreService.set(ContentControlToFieldMapService.FIELD_CONTEXTS_KEY, serializedFieldContexts);
    }

    this.loggingService.log('ContentControlToFieldMapService', 'upgrade', `upgrade complete.${!upgraded ? ' No changes.' : ''}`);
  }
}
